import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { AddLayerService } from '@connect-services/client/services/add-layer.service';
import { CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import { GroupBucket, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import {
    NO_GROUP,
    SelectByGroupComponent,
} from '@connect-services/src/lib/components/select-by-group/select-by-group.component';
import { DepositFolder, StandardsService } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LoadingComponent, RessourcesModule, SelectByGroupComponent],
    selector: 'app-select-interstandard-step',
    templateUrl: './select-interstandard-step.component.html',
})
export class SelectInterstandardStepComponent {
    public readonly isLoading = signal<boolean>(false);
    protected readonly addLayerService = inject(AddLayerService);
    protected readonly firstInterstandardDepositDefaultValue = computed(() =>
        this.addLayerService.getDefaultValue(
            this.addLayerService.firstInterstandardDepositFolderOptions(),
            this.addLayerService.selectedFirstInterstandardDepositFolder(),
        ),
    );
    protected readonly firstOptionsByGroup = signal<GroupBucket<DepositFolder> | null>(null);
    protected readonly secondInterstandardDepositDefaultValue = computed(() =>
        this.addLayerService.getDefaultValue(
            this.addLayerService.secondInterstandardDepositFolderOptions(),
            this.addLayerService.selectedSecondInterstandardDepositFolder(),
        ),
    );
    protected readonly secondOptionsByGroup = signal<GroupBucket<DepositFolder> | null>(null);

    private readonly standardsService = inject(StandardsService);

    public constructor() {
        effect(() => {
            const options = this.addLayerService.firstInterstandardDepositFolderOptions();
            if (!options && !this.addLayerService.selectedFirstInterstandardDepositFolder()) {
                return;
            }

            this.firstOptionsByGroup.set(this.groupByStandard(options));
        });
        effect(() => {
            const options = this.addLayerService.secondInterstandardDepositFolderOptions();
            if (!options && !this.addLayerService.selectedSecondInterstandardDepositFolder()) {
                return;
            }

            this.secondOptionsByGroup.set(this.groupByStandard(options));
        });
    }

    public async selectFirstInterstandardDepositFolder(depositFolder: DepositFolder[]): Promise<void> {
        this.addLayerService.selectedFirstInterstandardDepositFolder.set(depositFolder[0]);

        this.addLayerService.selectedSecondInterstandardDepositFolder.set(undefined);

        await this.fetchLinkedDepositFolder();
    }

    public selectSecondInterstandardDepositFolder(depositFolder: DepositFolder[]): void {
        this.addLayerService.selectedSecondInterstandardDepositFolder.set(depositFolder[0]);
    }

    private async fetchLinkedDepositFolder(): Promise<void> {
        const standard = this.addLayerService.selectedFirstInterstandardDepositFolder()?.standard;

        if (!standard) {
            return;
        }

        this.isLoading.set(true);

        this.addLayerService.linkedStandards.set(
            await firstValueFrom(this.standardsService.standardControllerGetLinkedStandards({ id: standard.id })),
        );

        this.initSelectSecondInterstandardDepositFolder();
        this.isLoading.set(false);
    }

    private groupByStandard(options: SelectOption<DepositFolder>[]): GroupBucket<DepositFolder> | null {
        if (!options?.length) {
            return null;
        }

        return options.reduce((acc: GroupBucket<DepositFolder>, depositOption: SelectOption<DepositFolder>) => {
            const groupName = depositOption.value.standard?.name ?? NO_GROUP;

            if (!acc[groupName]) {
                acc[groupName] = [];
            }

            acc[groupName].push(depositOption);

            return acc;
        }, {});
    }

    private initSelectSecondInterstandardDepositFolder(): void {
        if (!this.addLayerService.depositFolderByStandard() || !this.addLayerService.linkedStandards().length) {
            return;
        }

        const linkedDepositFolders = this.addLayerService
            .linkedStandards()
            .reduce((acc: DepositFolder[], currentStandard) => {
                return [...acc, ...(this.addLayerService.depositFolderByStandard()?.[currentStandard.id] ?? [])];
            }, []);

        this.addLayerService.secondInterstandardDepositFolderOptions.set(
            linkedDepositFolders.reduce((acc: SelectOption<DepositFolder>[], depositFolder) => {
                return [...acc, { label: depositFolder.name, state: CheckboxState.UNCHECKED, value: depositFolder }];
            }, []),
        );
    }
}
